import React, { useMemo, useState } from 'react';
import { Tabs, Typography } from 'antd';
import { contentService, seasonService, seriesService } from 'content/service';
import { contractContentService } from 'contract/services/contractTermItemService';
import TransferComponent, {
  TransferDataHandlerInterface,
  TransferDataSourceInterface,
} from 'components/transfer/Transfer';
import { SeriesInterface } from 'content/type/seriesType';
import { SeasonInterface } from 'type/seasonType';
import { ContentInterface } from 'content/type/contentType';

type MappingType = 'series' | 'season' | 'content';

const mappingTypes: { label: string; value: MappingType }[] = [
  { label: 'Series', value: 'series' },
  { label: 'Season', value: 'season' },
  { label: 'Content', value: 'content' },
];

const columns = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 100 },
  { title: 'Name', dataIndex: 'name', key: 'name' },
  { title: 'Name_en', dataIndex: 'name_en', key: 'name_en' },
  { title: 'Type', dataIndex: 'type', key: 'type', width: 100 },
];

interface ContentsMappingProps {
  contractId: number;
  termId: number | null;
  isNewTerm?: boolean;
}

const ContentsMapping = ({ contractId: _, termId, isNewTerm = false }: ContentsMappingProps) => {
  const [mappingType, setMappingType] = useState<MappingType>('series');

  // filterOption: search term filtering function (example)
  const filterOption = (inputValue: string, option: TransferDataSourceInterface) => {
    return option.name.toLowerCase().includes(inputValue.toLowerCase());
  };

  // dataHandler: define data handler for TransferComponent
  const dataHandler: TransferDataHandlerInterface = useMemo(
    () => ({
      fetchSourceData: async (query: Record<string, string>) => {
        let data: TransferDataSourceInterface[] = [];
        let count = 0;

        if (mappingType === 'series') {
          const res = await seriesService.fetch(new URLSearchParams(query));
          data = (res.results || []).map((item: SeriesInterface) => ({
            id: item.id,
            key: item.id,
            name: item.title,
            name_en: item.title_en,
            disabled: false,
            type: 'series' as const,
          }));
          count = res.count || 0;
        } else if (mappingType === 'season') {
          const res = await seasonService.fetch(new URLSearchParams(query));
          data = (res.results || []).map((item: SeasonInterface) => ({
            id: item.id,
            key: item.id,
            name: item.title,
            name_en: item.title_en,
            disabled: false,
            type: 'season' as const,
          }));
          count = res.count || 0;
        } else {
          const res = await contentService.fetch(new URLSearchParams(query));
          data = (res.results || []).map((item: ContentInterface) => ({
            id: item.id,
            key: item.id,
            name: item.title,
            name_en: item.title_en,
            disabled: false,
            type: 'content' as const,
          }));
          count = res.count || 0;
        }
        return {
          data,
          count,
        };
      },
      fetchTargetData: async query => {
        // API to fetch actually registered items
        // Do not call API if termId is missing, negative (new term), or is a new term
        if (!termId || termId < 0 || isNewTerm) {
          return { data: [], count: 0 };
        }
        const res = await contractContentService.fetchTermItems(_, termId, {
          ...query,
          type: mappingType,
        });
        return {
          data: (res.results || []).map(item => ({
            id: item.id,
            key: item.id,
            name: item.title,
            disabled: false,
            type: item.type,
          })),
          count: res.count || 0,
        };
      },
      createWithParentId: async (ids: number[]) => {
        // Call register API
        // Do not call API if termId is missing, negative (new term), or is a new term
        if (!termId || termId < 0 || isNewTerm) return;
        await contractContentService.createItemsForContractTerm(_, termId, mappingType, ids);
      },
      deleteWithParentId: async (ids: number[]) => {
        // Call unlink API
        // Do not call API if termId is missing, negative (new term), or is a new term
        if (!termId || termId < 0 || isNewTerm) return;
        await contractContentService.deleteItemsForContractTerm(_, termId, mappingType, ids);
      },
    }),
    [mappingType, termId, isNewTerm]
  );

  return (
    <div style={{ marginTop: 32 }}>
      <Typography.Title level={5} style={{ marginBottom: 12 }}>
        Contents Mapping
      </Typography.Title>

      {!termId || isNewTerm ? (
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: '#fafafa',
            border: '1px dashed #d9d9d9',
            borderRadius: '8px',
          }}
        >
          <Typography.Text type="secondary" style={{ fontSize: '16px' }}>
            {isNewTerm
              ? 'Please save the Term first before Contents Mapping is available.'
              : 'Please select a Term before Contents Mapping is available.'}
          </Typography.Text>
          <br />
          <Typography.Text type="secondary" style={{ fontSize: '14px', marginTop: '8px' }}>
            {isNewTerm
              ? 'Please enter Term information above and click the Save button.'
              : 'Please select a term from the Select Term dropdown.'}
          </Typography.Text>
        </div>
      ) : (
        // Tabs instead of a radio group: the selected type applies to both lists, not just the left one
        <Tabs
          type="card"
          activeKey={mappingType}
          onChange={key => setMappingType(key as MappingType)}
          items={mappingTypes.map(({ label, value }) => ({
            key: value,
            label,
            // Render only the active tab: dataHandler is bound to the current mappingType
            children:
              value === mappingType ? (
                <TransferComponent
                  key={`${mappingType}-${termId}`}
                  dataHandler={dataHandler}
                  filterOption={filterOption}
                  disabled={false}
                  columns={columns}
                />
              ) : null,
          }))}
        />
      )}
    </div>
  );
};

export default ContentsMapping;
